
import { createAsyncThunk } from "@reduxjs/toolkit";
import { isMalwareDetected, handleMalwareDetection } from "../../app/utils/security";

const CHUNK_SIZE = 512 * 1024; // 512KB chunks for better performance while staying within safe limits

// Helper to convert Base64 Data URI to Uint8Array
function dataURItoUint8Array(dataURI: string): Uint8Array {
    // Check if it's a data URI or raw base64
    let base64 = dataURI;
    if (dataURI.includes('base64,')) {
        base64 = dataURI.split(',')[1];
    }

    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

/**
 * Generic Chunked Upload Thunk
 * Uploads a file (or base64 string) and returns a fileId
 */
export const uploadFileChunked = createAsyncThunk(
    "chunkUpload/uploadFile",
    async (
        {
            fileData,
            fileName,
            mimeType,
            onProgress
        }: {
            fileData: File | string | Uint8Array,
            fileName: string,
            mimeType?: string,
            onProgress?: (progress: { current: number; total: number }) => void
        },
        { rejectWithValue }
    ) => {
        try {
            // Step 1: Convert input to Uint8Array
            let uint8Array: Uint8Array;

            if (fileData instanceof File) {
                const arrayBuffer = await fileData.arrayBuffer();
                uint8Array = new Uint8Array(arrayBuffer);
            } else if (typeof fileData === 'string') {
                // Assume Base64 string or Data URI
                uint8Array = dataURItoUint8Array(fileData);
            } else if (fileData instanceof Uint8Array) {
                uint8Array = fileData;
            } else {
                throw new Error("Unsupported file data format");
            }

            const fileSize = uint8Array.length;
            const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);

            console.log(`📦 Generic Chunk Upload: ${fileName} (${fileSize} bytes) in ${totalChunks} chunks`);

            // Use relative paths or construct based on window.location if CONSTANTS are missing
            // Assuming the app uses a standard API prefix, we'll try to use relative /api/upload/...
            // The existing slice used ROUTES.importFromExcel and replaced, but we can just use the path directly
            // if we are mindful of the base URL.
            // However, the app seems to use axios or fetch with credentials. 
            // In ImportFromExcelChunkedSlice, it used fetch with full URL logic?
            // "ROUTES.importFromExcel" was typically a full URL or path.
            // Let's assume we can use the same base.

            // To be safe, we'll look for the API base.
            // But strict relative paths '/api/...' allow the proxy to handle it in dev.
            const API_BASE = '/api';

            // Step 2: Initialize
            const initResponse = await fetch(`${API_BASE}/upload/init`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ fileName, totalChunks, fileSize }),
                credentials: "include",
            });

            if (!initResponse.ok) {
                const err = await initResponse.json().catch(() => ({}));
                throw new Error(err.message || "Init failed");
            }
            const { sessionId } = await initResponse.json();

            // Step 3: Upload Chunks
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, fileSize);
                const chunk = uint8Array.slice(start, end);

                // Convert to plain base64 for JSON transport
                // Using looping instead of spread/apply to avoid stack overflow on large chunks
                let binary = '';
                const len = chunk.byteLength;
                for (let k = 0; k < len; k++) {
                    binary += String.fromCharCode(chunk[k]);
                }
                const base64Chunk = btoa(binary);

                const chunkResponse = await fetch(`${API_BASE}/upload/chunk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ sessionId, chunkIndex: i, chunkData: base64Chunk }),
                    credentials: "include",
                });

                if (!chunkResponse.ok) {
                    throw new Error(`Chunk ${i} failed`);
                }

                // Progress callback
                if (typeof onProgress === 'function') {
                    onProgress({ current: i + 1, total: totalChunks });
                }
            }

            // Step 4: Finalize Generic
            const finalizeResponse = await fetch(`${API_BASE}/upload/finalize-generic`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sessionId }),
                credentials: "include",
            });

            // Check for malware detection (backend returns 403 with malwareDetected flag)
            if (isMalwareDetected(finalizeResponse.status)) {
                try {
                    const malwareData = await finalizeResponse.json();
                    if (malwareData?.malwareDetected) {
                        return await handleMalwareDetection();
                    }
                } catch {
                    return await handleMalwareDetection();
                }
            }

            if (!finalizeResponse.ok) {
                const err = await finalizeResponse.json().catch(() => ({}));
                throw new Error(err.message || "Finalize generic failed");
            }

            const result = await finalizeResponse.json();
            // result should contain { success: true, fileId: "TEMP_FILE_..." }
            console.log("✅ Generic upload complete:", result);

            return result.fileId; // Return only the ID
        } catch (error: any) {
            console.error("Generic Chunk Upload Error:", error);
            return rejectWithValue(error.message);
        }
    }
);
